import React from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../../components/AppIcon';
import Button from '../../../../components/ui/Button';
import { RefereeCertification, resolveEnum } from '@/models/tournament';
import type { RefereeView } from '@/models/tournament';
import { useListboxKeyboard } from '../hooks/useListboxKeyboard';

interface RefereeListPanelProps {
  referees: RefereeView[];
  selectedReferee: RefereeView | null;
  onSelectReferee: (referee: RefereeView) => void;
  /** Drives the empty state: filters applied means "narrow your search", not "there is nothing". */
  hasActiveFilters?: boolean;
  onClearFilters?: () => void;
}

const certificationKeys: Record<RefereeCertification, string> = {
  [RefereeCertification.NationalLevelA]: 'National Level A',
  [RefereeCertification.AFCElite]: 'AFC Elite',
  [RefereeCertification.FIFAInternational]: 'FIFA International',
};

// Same pill vocabulary as referee-management's table, so a referee looks the same in both places.
const certificationColors: Record<RefereeCertification, string> = {
  [RefereeCertification.NationalLevelA]: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  [RefereeCertification.AFCElite]: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300',
  [RefereeCertification.FIFAInternational]: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
};

const RefereeListPanel: React.FC<RefereeListPanelProps> = ({
  referees,
  selectedReferee,
  onSelectReferee,
  hasActiveFilters = false,
  onClearFilters,
}) => {
  const { t } = useTranslation('tournament');

  // Roving tab stop starts on the currently-selected referee so returning to the panel by Tab
  // lands where the user left off, not back at the top.
  const selectedIndex = referees?.findIndex((referee) => referee?.id === selectedReferee?.id) ?? -1;
  const selectByIndex = React.useCallback(
    (index: number) => {
      const referee = referees?.[index];
      if (referee) onSelectReferee(referee);
    },
    [referees, onSelectReferee],
  );
  const { getOptionProps } = useListboxKeyboard(referees?.length ?? 0, selectByIndex, selectedIndex);

  if (referees?.length === 0) {
    return (
      <div className="bg-card border border-border rounded-lg p-8 text-center" data-testid="referee-empty-state">
        <Icon name={hasActiveFilters ? 'SearchX' : 'Users'} size={48} className="mx-auto text-muted-foreground/40 mb-4" />
        <h3 className="text-lg font-semibold text-foreground mb-2">
          {hasActiveFilters ? t('No referees found') : t('emptyState.noRefereesYet')}
        </h3>
        <p className="text-sm text-muted-foreground">
          {hasActiveFilters ? t('Try adjusting your filters') : t('emptyState.noRefereesYetDesc')}
        </p>
        {hasActiveFilters && onClearFilters && (
          <Button variant="outline" size="sm" onClick={onClearFilters} iconName="X" iconSize={14} className="mt-4 mx-auto">
            {t('Clear Filters')}
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="bg-card border border-border rounded-lg p-4" data-testid="referee-list-panel">
        <h2 id="referee-list-heading" className="text-lg font-semibold text-foreground flex items-center mb-4">
          <Icon name="Users" size={20} className="me-2 text-primary" />
          {t('Available Referees')}
          <span className="ms-auto text-sm font-normal text-muted-foreground">({referees?.length})</span>
        </h2>

        {/* This is one of the page's two primary selectors, and every card used to be a bare
            <div onClick> — no role, no tabIndex, no key handler — so a keyboard-only user could not
            pick a referee AT ALL and therefore could not assign one. The first repair added the
            listbox/option roles but bound only Enter and Space, so arrows, Home and End were still
            dead and focus stayed wherever Tab dropped it. Full keyboard contract now lives in
            ../hooks/useListboxKeyboard.
            The nested max-height scroller is confined to `lg`: below it, a scroll region inside the
            page scroll trapped the user in list 1 of 3 on a phone. */}
        <div
          role="listbox"
          aria-labelledby="referee-list-heading"
          className="space-y-3 lg:max-h-[calc(100vh-300px)] lg:overflow-y-auto lg:pe-2"
        >
          {referees?.map((referee, index) => {
            const isSelected = selectedReferee?.id === referee?.id;
            return (
              <div
                key={referee?.id}
                role="option"
                aria-selected={isSelected}
                onClick={() => onSelectReferee(referee)}
                {...getOptionProps(index)}
                className={`border rounded-lg p-3 cursor-pointer transition-colors duration-200 motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 ${
                  isSelected
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-primary/50 hover:bg-muted/40'
                }`}
                data-testid={`referee-card-${referee?.id}`}
              >
                {/* Flattened: the card used to nest a tinted certification box and two grey stat
                    boxes inside itself inside the panel card — four surfaces deep for a name, a
                    badge and two numbers. Avatar + name + one pill + one muted line. */}
                <div className="flex items-start gap-3">
                  {referee?.photoUrl ? (
                    <img
                      src={referee.photoUrl}
                      alt={t('{{name}} profile photo', { name: referee?.name })}
                      className="w-10 h-10 rounded-full object-cover shrink-0"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center shrink-0">
                      <Icon name="User" size={16} className="text-amber-600 dark:text-amber-400" />
                    </div>
                  )}

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-sm font-semibold text-foreground truncate">{referee?.name}</h3>
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          certificationColors[(resolveEnum(RefereeCertification, referee?.certification) as RefereeCertification)] ?? 'bg-muted text-muted-foreground'
                        }`}
                      >
                        {t(certificationKeys[(resolveEnum(RefereeCertification, referee?.certification) as RefereeCertification)] ?? 'Unknown')}
                      </span>
                    </div>

                    <p className="text-xs text-muted-foreground mt-1 truncate">
                      {referee?.totalMatches} {t('Matches')}
                      <span className="mx-1.5 text-muted-foreground/60">·</span>
                      {referee?.rating}★
                      {referee?.location && (
                        <>
                          <span className="mx-1.5 text-muted-foreground/60">·</span>
                          {referee.location}
                        </>
                      )}
                    </p>
                  </div>
                </div>

                {/* Contact Info (shown when selected) */}
                {isSelected && (
                  <div className="mt-3 pt-3 border-t border-border space-y-1.5">
                    <div className="flex items-center text-xs text-muted-foreground">
                      <Icon name="Phone" size={12} className="me-2 text-muted-foreground/70" />
                      <span>{referee?.phone}</span>
                    </div>
                    <div className="flex items-center text-xs text-muted-foreground">
                      <Icon name="Mail" size={12} className="me-2 text-muted-foreground/70" />
                      <span className="truncate">{referee?.email}</span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default RefereeListPanel;
